// src/pages/imamMahdi/Supporters.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Supporters = () => {
  return (
    <>
      <Helmet>
        <title>What It Means to Be Among Imam Mahdi’s Supporters | Ziyarat Guide</title>
        <meta
          name="description"
          content="Learn the true qualities of Imam Mahdi’s (aj) supporters — courage, sincerity, sacrifice, and deep spirituality — and how to prepare yourself to stand with him."
        />
        <meta
          property="og:title"
          content="What It Means to Be Among Imam Mahdi’s Supporters | Ziyarat Guide"
        />
        <meta
          property="og:description"
          content="Discover the noble traits and daily actions that make a believer worthy to be among Imam Mahdi’s (aj) true companions when he reappears."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/08/seventh-What-it-means-to-be-among-his-supporters-.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">
            What It Means to Be Among Imam Mahdi’s Supporters
          </h1>

          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/08/seventh-What-it-means-to-be-among-his-supporters-.png"
              alt="Supporters of Imam Mahdi"
              className="imam-image"
            />

            <h2>A Pledge of the Heart, Not Just the Tongue</h2>
            <p>
              In Shia belief, awaiting Imam Mahdi (peace be upon him) is not a
              passive hope. It is an active commitment to live by the values he
              will establish – so that when he appears, we are ready to stand
              under his banner without hesitation.
            </p>
            <p>
              Being one of his true supporters means embodying qualities that
              make us worthy of his cause.
            </p>

            <h2>Qualities of His Companions</h2>
            <p>
              The narrations from the Ahlulbayt (as) describe those who will
              stand with Imam Mahdi as people of exceptional moral and spiritual
              calibre. Among the qualities frequently emphasised are:
            </p>

            <h3>Courage</h3>
            <p>
              They will be fearless in the face of injustice and oppression.
              They will not hesitate to speak the truth or defend the rights of
              the oppressed, even when it comes at personal cost.
            </p>

            <h3>Sincerity</h3>
            <p>
              Their allegiance is pure, without hypocrisy or self-interest. They
              live with integrity, whether seen or unseen, and their loyalty to
              Allah and the Imam is unwavering.
            </p>

            <h3>Readiness to Sacrifice</h3>
            <p>
              They will be willing to give of their time, wealth, and even lives
              for the establishment of divine justice.
            </p>
            <blockquote>
              “Whoever would love to be among the companions of the Qa’im, then
              let him wait [live with piety], act with good morals while
              awaiting. If he dies before his Qa’im arises, he will have the
              reward of one who has witnessed his appearance.”
            </blockquote>

            <h3>Deep Spirituality</h3>
            <p>
              They are people of prayer, remembrance, and Qur’an. They cultivate
              closeness to Allah daily, ensuring their hearts are not attached
              to worldly greed or corruption.
            </p>

            <h3>Concern for Humanity</h3>
            <p>
              Their aim is not narrow or selfish; they yearn for justice across
              the earth, for all peoples, regardless of background.
            </p>

            <h2>Preparing Ourselves Today</h2>
            <p>
              In practical terms, being among Imam Mahdi’s supporters means
              striving each day to:
            </p>
            <ul>
              <li>Be truthful and just in all dealings</li>
              <li>Help the vulnerable and speak against wrongdoing</li>
              <li>
                Guard our prayers, give charity, and treat others with compassion
              </li>
              <li>
                Keep alive the longing for his appearance by reciting
                supplications like Dua Al-Ahad and Dua Nudba
              </li>
            </ul>

            <p>
              To be among the supporters of Imam Mahdi (aj) is to live
              courageously, sincerely, and generously today, standing against
              oppression in every form. It means being ready to sacrifice for
              truth and to build a heart worthy of joining his noble mission when
              he appears.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li>
              <Link to="/imam-mahdi">Who is Imam Mahdi?</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/described-in-quran-hadith">
                Described In Quran & Hadith
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/how-to-talk">How To Talk</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/impact-appearance">
                Impact of His Appearance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/misconceptions">Misconceptions</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/practices-allegiance">
                Practices & Allegiance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/reflections-stories">
                Reflections & Stories
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/return">Return</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/signs-reappearance">
                Signs of Reappearance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/supporters">Supporters</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/why-occultation">Why Occultation</Link>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default Supporters;
